import { Injectable } from "@angular/core";
import { HttpClient, HttpHeaders, HttpParams } from "@angular/common/http";
import { Router } from "@angular/router";
import { defer, Observable, throwError } from "rxjs";
import { catchError, finalize } from "rxjs/operators";
import { MessageService } from "primeng/api";
import { AppService } from "../../../app.service";
import { BackOfficeStorageService } from "./storage.service";
import { LoadingService } from "../../services/loading.service";

interface BackOfficeErrorHandlerConfig {
  /**
   * Executes the centralized behavior for a handled HTTP status.
   *
   * Each status maps to one handler so individual components do not need their
   * own switch statements for common API failures.
   */
  handle: (error: any) => void;

  /**
   * Tells downstream mutation helpers to skip their generic failure toast.
   *
   * This prevents duplicate messages when this service has already shown the
   * backend-specific error to the user.
   */
  suppressMutationFailureToast?: boolean;
}

interface BackOfficeRequestOptions {
  /**
   * Allows background requests to opt out of toast handling.
   *
   * Example: health polling should return UP or DOWN quietly without notifying
   * the user every time the service is unavailable.
   */
  silentError?: boolean;

  /**
   * Controls the global loading overlay. It is enabled by default and should
   * only be disabled for background work that does not block the user.
   */
  showLoading?: boolean;
}

/**
 * Central API wrapper for backOffice backoffice calls.
 *
 * This service acts as the scoped interceptor for the backOffice module. All
 * backOffice services call through this wrapper, so common request headers,
 * auth token usage, and handled API error behavior stay in one place instead
 * of being reimplemented by individual components.
 *
 * Error handling flow:
 * 1. A failed HTTP response is received in handleBackOfficeResponse.
 * 2. Silent requests skip toast handling and rethrow the error to the caller.
 * 3. Non-silent requests are routed by HTTP status in handleErrorStatus.
 * 4. Supported statuses execute one centralized handler:
 *    400, 403, 404, 409, and 422 show an error toast.
 *    401 shows the session-expired toast and logs the user out.
 * 5. Toast details prefer backend-provided messages before fallback wording.
 * 6. The original error is rethrown so page-level loaders and refresh states
 *    can still react normally.
 */
@Injectable({
  providedIn: "root",
})
export class BackOfficeApiService {
  private isHandlingUnauthorized = false;
  private readonly errorHandlers: {
    [status: number]: BackOfficeErrorHandlerConfig;
  };

  /**
   * Initializes the backoffice API wrapper and builds the status handler map.
   *
   * The handler map is created once in the constructor so all request methods
   * use the same status-to-behavior rules for the lifetime of the service.
   */
  constructor(
    private http: HttpClient,
    private storageService: BackOfficeStorageService,
    private messageService: MessageService,
    private router: Router,
    private appService: AppService,
    private loadingService: LoadingService,
  ) {
    this.errorHandlers = this.createErrorHandlers();
  }

  /**
   * Builds the standard headers for every backoffice API request.
   *
   * The bearer token is read through getToken so all backOffice API services
   * share the same authentication source. Keeping headers centralized also
   * avoids individual services drifting on Content-Type, Accept, or token
   * formatting.
   */
  private getHeaders(): HttpHeaders {
    return new HttpHeaders({
      "Content-Type": "application/json",
      Accept: "application/json",
      Authorization: `Bearer ${this.getToken()}`,
    });
  }

  /**
   * Reads the current backoffice token from backOffice storage.
   *
   * The method returns an empty string when no token exists so request creation
   * remains predictable. Authentication failures are then handled consistently
   * by the 401 handler after the API responds.
   */
  private getToken(): string {
    const user: StoredUser | string = this.storageService.get("token", "");
    return (user as any as StoredUser)?.token || "";
  }

  /**
   * Sends a GET request with standard backoffice headers and optional query
   * parameters.
   *
   * All responses pass through handleBackOfficeResponse so handled errors are
   * toastified in one place. Use requestOptions.silentError for background
   * refreshes that should fail quietly.
   */
  get<T>(
    url: string,
    params?: any,
    requestOptions: BackOfficeRequestOptions = {},
  ): Observable<T> {
    const options = {
      headers: this.getHeaders(),
      params: new HttpParams({ fromObject: params || {} }),
    };

    return this.handleBackOfficeResponse(
      this.http.get<T>(url, options),
      requestOptions,
    );
  }

  /**
   * Sends a POST request with a JSON body, standard headers, and optional query
   * parameters.
   *
   * This is used for create and update style backoffice endpoints that are
   * exposed as POST APIs. Error status handling remains centralized and the
   * original error is still rethrown for caller-specific state cleanup.
   */
  post<T>(
    url: string,
    body: any,
    params?: any,
    requestOptions: BackOfficeRequestOptions = {},
  ): Observable<T> {
    const options = {
      headers: this.getHeaders(),
      params: new HttpParams({ fromObject: params || {} }),
    };

    return this.handleBackOfficeResponse(
      this.http.post<T>(url, body, options),
      requestOptions,
    );
  }

  /**
   * Sends a PUT request with a JSON body, standard headers, and optional query
   * parameters.
   *
   * Components and feature services do not need their own status-specific error
   * handling because this method routes failures through the shared response
   * handler.
   */
  put<T>(
    url: string,
    body: any,
    params?: any,
    requestOptions: BackOfficeRequestOptions = {},
  ): Observable<T> {
    const options = {
      headers: this.getHeaders(),
      params: new HttpParams({ fromObject: params || {} }),
    };

    return this.handleBackOfficeResponse(
      this.http.put<T>(url, body, options),
      requestOptions,
    );
  }

  /**
   * Sends a PATCH request with a JSON body, standard headers, and optional
   * query parameters.
   *
   * Permission updates and partial updates use this method so they receive the
   * same auth, toast, and rethrow behavior as other backoffice requests.
   */
  patch<T>(
    url: string,
    body: any,
    params?: any,
    requestOptions: BackOfficeRequestOptions = {},
  ): Observable<T> {
    const options = {
      headers: this.getHeaders(),
      params: new HttpParams({ fromObject: params || {} }),
    };

    return this.handleBackOfficeResponse(
      this.http.patch<T>(url, body, options),
      requestOptions,
    );
  }

  /**
   * Sends a DELETE request with support for a request body.
   *
   * Angular's HttpClient delete helper has limited body ergonomics, so this
   * uses http.request directly. The rest of the flow is still identical:
   * attach standard headers, add optional query parameters, handle known error
   * statuses centrally, and rethrow the original error.
   */
  delete<T>(
    url: string,
    body,
    params?: any,
    requestOptions: BackOfficeRequestOptions = {},
  ): Observable<T> {
    return this.handleBackOfficeResponse(
      this.http.request<T>("DELETE", url, {
        headers: this.getHeaders(),
        params: new HttpParams({ fromObject: params || {} }),
        body: body,
      }),
      requestOptions,
    );
  }

  /**
   * Wraps an HTTP request with centralized backoffice error handling.
   *
   * This is the interception point for the backOffice module. If silentError
   * is true, the error is rethrown without a toast. Otherwise the error is sent
   * to handleErrorStatus before being rethrown. Rethrowing is important because
   * page components still need to show page-level error states or reset local
   * form state after a failed request.
   */
  private handleBackOfficeResponse<T>(
    request: Observable<T>,
    requestOptions: BackOfficeRequestOptions = {},
  ): Observable<T> {
    return defer(() => {
      const showLoading = requestOptions.showLoading !== false;

      if (showLoading) {
        this.loadingService.show();
      }

      return request.pipe(
        catchError((error) => {
          if (!requestOptions.silentError) {
            this.handleErrorStatus(error);
          }

          return throwError(error);
        }),
        finalize(() => {
          if (showLoading) {
            this.loadingService.hide();
          }
        }),
      );
    });
  }

  /**
   * Defines the handled HTTP status map for backoffice errors.
   *
   * The status code is the first priority because it decides the category and
   * behavior of the failure:
   * 400 means the request cannot be processed as sent.
   * 401 means the session is invalid and the user must be logged out.
   * 403 means the user is authenticated but not allowed to perform the action.
   * 404 means the requested resource was not found.
   * 409 means the requested change conflicts with existing backend state.
   * 422 means the request shape is accepted but semantic validation failed.
   *
   * Each handled status suppresses mutation-flow fallback toasts so the user
   * sees one clear backend-specific message instead of two competing messages.
   */
  private createErrorHandlers(): {
    [status: number]: BackOfficeErrorHandlerConfig;
  } {
    return {
      400: {
        handle: (error) =>
          this.showErrorToast(
            error,
            "Bad request",
            "The request could not be processed. Please check the entered details and try again.",
          ),
        suppressMutationFailureToast: true,
      },
      401: {
        handle: (error) => this.handleUnauthorized(error),
        suppressMutationFailureToast: true,
      },
      403: {
        handle: (error) =>
          this.showErrorToast(
            error,
            "Access denied",
            "You do not have permission to perform this action.",
          ),
        suppressMutationFailureToast: true,
      },
      404: {
        handle: (error) =>
          this.showErrorToast(
            error,
            "Not found",
            "The requested information was not found.",
          ),
        suppressMutationFailureToast: true,
      },
      409: {
        handle: (error) =>
          this.showErrorToast(
            error,
            "Conflict",
            "This action conflicts with existing data. Please refresh and try again.",
          ),
        suppressMutationFailureToast: true,
      },
      422: {
        handle: (error) =>
          this.showErrorToast(
            error,
            "Validation error",
            "The request is valid, but some details need attention.",
          ),
        suppressMutationFailureToast: true,
      },
    };
  }

  /**
   * Selects the error handler for a failed response based on HTTP status.
   *
   * Unknown statuses are intentionally left alone so they can continue through
   * existing page-level error states without this service inventing behavior
   * that was not requested. Known statuses are tagged on the error object before
   * the handler runs. Those flags let downstream helpers know that the error
   * was already handled by the centralized backoffice layer.
   */
  private handleErrorStatus(error: any): void {
    const status = Number(error?.status);
    const handlerConfig = this.errorHandlers[status];

    if (!handlerConfig) {
      return;
    }

    error.backOfficeErrorHandled = true;
    error.backOfficeSuppressMutationFailureToast =
      !!handlerConfig.suppressMutationFailureToast;

    handlerConfig.handle(error);
  }

  /**
   * Handles 401 responses by notifying the user and returning them to login.
   *
   * A short guard prevents multiple simultaneous failed requests from producing
   * repeated logout toasts and repeated navigation attempts. The original error
   * remains tagged with backOfficeUnauthorizedHandled so mutation flows can
   * stop quietly after logout starts.
   */
  private handleUnauthorized(error: any): void {
    error.backOfficeUnauthorizedHandled = true;

    if (this.isHandlingUnauthorized) {
      return;
    }

    this.isHandlingUnauthorized = true;
    this.messageService.add({
      severity: "error",
      summary: "Session expired",
      detail: "Your backoffice session has expired. Please log in again.",
    });

    localStorage.clear();
    this.appService.setAppMode("REPORTING");
    this.router.navigate(["/login"]);

    setTimeout(() => {
      this.isHandlingUnauthorized = false;
    }, 1500);
  }

  /**
   * Shows a status-specific error toast for handled non-401 failures.
   *
   * The summary comes from the status handler so the user gets a stable category
   * such as "Conflict" or "Validation error". The detail comes from
   * getErrorMessage, which prefers backend wording over local fallback wording.
   */
  private showErrorToast(
    error: any,
    summary: string,
    fallbackMessage: string,
  ): void {
    this.messageService.add({
      severity: "error",
      summary,
      detail: this.getErrorMessage(error, fallbackMessage),
    });
  }

  /**
   * Resolves the most useful message to show in the toast detail.
   *
   * Message priority is deliberately backend-first:
   * 1. Response header "message" or "Message".
   * 2. Response body field "message".
   * 3. Response body field "error".
   * 4. Response body "details" array joined into a readable string.
   * 5. Raw string response body.
   * 6. Local fallback wording for the status.
   *
   * This keeps the UI honest to the API response when the backend provides a
   * precise business error, while still giving users a useful message when the
   * backend response is sparse.
   */
  private getErrorMessage(error: any, fallbackMessage: string): string {
    const headerMessage = this.getHeaderMessage(error);

    if (headerMessage) {
      return headerMessage;
    }

    const body = error?.error;

    if (body?.message) {
      return body.message;
    }

    if (body?.error) {
      return body.error;
    }

    if (Array.isArray(body?.details) && body.details.length) {
      return body.details.join(", ");
    }

    if (typeof body === "string" && body.trim()) {
      return body;
    }

    return fallbackMessage;
  }

  /**
   * Reads a backend error message from response headers.
   *
   * Some existing APIs send business error text in the "message" header instead
   * of the JSON body. Header text is checked first in getErrorMessage because it
   * is often the most specific message supplied by those endpoints.
   */
  private getHeaderMessage(error: any): string {
    const headers = error?.headers;

    if (!headers?.get) {
      return "";
    }

    return headers.get("message") || headers.get("Message") || "";
  }
}

interface StoredUser {
  token: string;
}
